import { FC, useMemo, useState } from 'react';
import { Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import type { ForecastingRow } from '@/api/forecasting';
import type { BuyingPointRow, DashboardFilters, KpiLens } from '../forecasting.types';
import { FORECASTING_THEME } from '../forecastingTheme';
import { formatCurrency, formatPercent } from '../utils/forecastingAggregations';
import {
  canDrillDownLevel,
  canDrillUpLevel,
  DRILL_LEVELS,
  getDrillLevelConfig,
  gpVsBudgetPercent,
  groupDrilldownTable,
  sumDrilldownTotals,
  UNKNOWN_DIMENSION
} from '../utils/drilldownTable';
import { DrillHeader } from './DrillHeader';
import { FORECASTING_TABLE_CSS } from '../forecastingTableStyles';
import { ChartTitle, DrillToolbar, Panel } from '../Forecasting.styles';

interface BuyingPointTableProps {
  rows: ForecastingRow[];
  lens: KpiLens;
  filters: DashboardFilters;
  onFilterChange: (patch: Partial<DashboardFilters>) => void;
}

/** GP against budget, coloured by whether it is ahead of or behind it. */
function VsBudgetCell({ value }: { value: number | null }) {
  if (value == null) return <span style={{ color: FORECASTING_THEME.textMuted }}>—</span>;
  const color = value >= 100 ? FORECASTING_THEME.positive : FORECASTING_THEME.negative;
  return <span style={{ color }}>{formatPercent(value, 0)}</span>;
}

export const BuyingPointTable: FC<BuyingPointTableProps> = ({ rows, lens, filters, onFilterChange }) => {
  const [levelIndex, setLevelIndex] = useState(0);

  const level = useMemo(() => getDrillLevelConfig(levelIndex), [levelIndex]);
  const data = useMemo(
    () => groupDrilldownTable(rows, levelIndex, lens),
    [rows, levelIndex, lens]
  );

  /* Clicking a row filters the whole page to it through the sidebar filter
     for this level, so the chart and cards follow and the pick can be seen
     and cleared there. Clicking more rows adds them, e.g. every McCann. */
  const picked = filters[level.filterKey];
  const isPicked = (label: string) => Boolean(picked?.includes(label));
  const toggleRow = (label: string) => {
    if (label === UNKNOWN_DIMENSION) return;
    const next = isPicked(label) ? (picked ?? []).filter((v) => v !== label) : [...(picked ?? []), label];
    onFilterChange({ [level.filterKey]: next.length ? next : undefined });
  };

  const canUp = canDrillUpLevel(levelIndex);
  const canDown = canDrillDownLevel(levelIndex);

  const drillUp = () => setLevelIndex((i) => Math.max(0, i - 1));
  const drillDown = () => setLevelIndex((i) => Math.min(DRILL_LEVELS.length - 1, i + 1));

  const columns: ColumnsType<BuyingPointRow> = useMemo(
    () => [
      {
        title: (
          <DrillHeader
            title={level.title}
            canUp={canUp}
            canDown={canDown}
            onDrillUp={drillUp}
            onDrillDown={drillDown}
          />
        ),
        dataIndex: 'label',
        key: 'label',
        ellipsis: true
      },
      {
        title: (
          <DrillHeader
            title="Revenue"
            canUp={canUp}
            canDown={canDown}
            onDrillUp={drillUp}
            onDrillDown={drillDown}
          />
        ),
        dataIndex: 'revenue',
        key: 'revenue',
        align: 'right',
        onHeaderCell: () => ({ className: 'forecasting-drill-header-cell' }),
        sorter: (a, b) => a.revenue - b.revenue,
        defaultSortOrder: 'descend',
        render: (v: number) => formatCurrency(v)
      },
      {
        title: (
          <DrillHeader
            title="GP"
            canUp={canUp}
            canDown={canDown}
            onDrillUp={drillUp}
            onDrillDown={drillDown}
          />
        ),
        dataIndex: 'gp',
        key: 'gp',
        align: 'right',
        onHeaderCell: () => ({ className: 'forecasting-drill-header-cell' }),
        render: (v: number) => formatCurrency(v)
      },
      {
        title: 'GP % Split',
        dataIndex: 'gpSplit',
        key: 'gpSplit',
        align: 'right',
        render: (v: number) => formatPercent(v, 0)
      },
      {
        title: 'Margin',
        dataIndex: 'margin',
        key: 'margin',
        align: 'right',
        render: (v: number) => formatPercent(v, 1)
      },
      {
        title: 'GP vs Budget',
        dataIndex: 'gpVsBudgetPct',
        key: 'gpVsBudgetPct',
        align: 'right',
        render: (v: number | null) => <VsBudgetCell value={v} />
      }
    ],
    [level.title, canUp, canDown]
  );

  const grandTotal = useMemo(() => {
    const current = sumDrilldownTotals(rows, lens);
    return {
      revenue: current.revenue,
      gp: current.gp,
      margin: current.revenue > 0 ? (current.gp / current.revenue) * 100 : 0,
      gpVsBudgetPct: gpVsBudgetPercent(sumDrilldownTotals(rows).gp, sumDrilldownTotals(rows, 'budgetGp').gp)
    };
  }, [rows, lens]);

  return (
    <Panel>
      <DrillToolbar>
        <ChartTitle style={{ margin: 0 }}>{level.title}</ChartTitle>
        <div className="drill-actions">
          <span className="drill-hint">Drill level</span>
          <Tooltip title="Drill up">
            <button
              type="button"
              className="drill-btn"
              disabled={!canUp}
              onClick={drillUp}
              aria-label="Drill up"
            >
              <CaretUpOutlined />
            </button>
          </Tooltip>
          <Tooltip title="Drill down">
            <button
              type="button"
              className="drill-btn"
              disabled={!canDown}
              onClick={drillDown}
              aria-label="Drill down"
            >
              <CaretDownOutlined />
            </button>
          </Tooltip>
        </div>
      </DrillToolbar>
      <Table<BuyingPointRow>
        size="small"
        pagination={false}
        rowKey="key"
        columns={columns}
        onRow={(record) => ({
          onClick: () => toggleRow(record.label),
          style: record.label === UNKNOWN_DIMENSION ? undefined : { cursor: 'pointer' }
        })}
        rowClassName={(record) => (isPicked(record.label) ? 'forecasting-row-picked' : '')}
        dataSource={data}
        /* No horizontal scroll: six columns fit, so Margin and GP vs Budget
           are on screen without scrolling right. */
        tableLayout="fixed"
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}>Grand total</Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                {formatCurrency(grandTotal.revenue)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="right">
                {formatCurrency(grandTotal.gp)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3} align="right">
                100%
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} align="right">
                {formatPercent(grandTotal.margin, 1)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5} align="right">
                <VsBudgetCell value={grandTotal.gpVsBudgetPct} />
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
        className="forecasting-bp-table"
      />
      <style>{FORECASTING_TABLE_CSS}</style>
    </Panel>
  );
};
